import { cva } from '@vizit/theme';
import { ButtonLevel, ButtonSize } from './types';

export const buttonClasses = cva({
  base: [
    'inline-flex items-center justify-center whitespace-nowrap text-sm text-primary ring-offset-brand-600 transition-colors outline-none cursor-pointer',
    /* Focus Visible */
    'data-[focus-visible]:outline-none data-[focus-visible]:ring-offset-2 data-[focus-visible]:ring-offset-white data-[focus-visible]:ring-2 data-[focus-visible]:ring-brand-600',
  ],
  variants: {
    rounded: {
      true: 'rounded-full',
      false: '',
    },
    level: {
      [ButtonLevel.Danger]: 'bg-error-600 inset-shadow-lg inset-shadow-error-900 text-primary-on-brand',
      [ButtonLevel.Primary]: 'bg-brand-solid inset-shadow-lg inset-shadow-brand-900 text-primary-on-brand',
      [ButtonLevel.Secondary]: 'border border-primary bg-primary text-gray-light-700 rounded-xs',
      [ButtonLevel.Ghost]: 'text-gray-light-700',
      [ButtonLevel['Text-Primary']]: 'text-brand-secondary',
      [ButtonLevel['Text-Secondary']]: 'text-tertiary',
      [ButtonLevel['Text-Danger']]: 'text-error-primary',
      [ButtonLevel.Gradient]: 'bg-gradient-neon text-white',
    },
    size: {
      [ButtonSize.Xl]: 'h-24 gap-3 text-md',
      [ButtonSize.Lg]: 'h-22 gap-3 text-md',
      [ButtonSize.Md]: 'h-20 gap-2 text-sm',
      [ButtonSize.Sm]: 'h-18 gap-2 text-sm',
      [ButtonSize.Xs]: 'h-16 gap-2 text-sm',
    },
    isIconOnly: {
      false: null,
      true: null,
    },
    isDisabled: {
      false: 'pointer-events-auto',
      true: null,
    },
    isLoading: {
      false: 'pointer-events-auto',
      true: null,
    },
  },
  defaultVariants: {
    rounded: false,
    level: ButtonLevel.Primary,
    size: ButtonSize.Sm,
  },
  compoundVariants: [
    {
      size: ButtonSize.Xl,
      isIconOnly: false,
      className: 'py-5 px-9',
    },
    {
      size: ButtonSize.Lg,
      isIconOnly: false,
      className: 'px-8 py-5',
    },
    {
      size: ButtonSize.Md,
      isIconOnly: false,
      className: 'px-7 py-5',
    },
    {
      size: ButtonSize.Sm,
      isIconOnly: false,
      className: 'px-6 py-4',
    },
    {
      size: ButtonSize.Xs,
      isIconOnly: false,
      className: 'px-5 py-3',
    },
    /* Icon Only */
    {
      size: ButtonSize.Xl,
      isIconOnly: true,
      className: 'p-7',
    },
    {
      size: ButtonSize.Lg,
      isIconOnly: true,
      className: 'p-6',
    },
    {
      size: ButtonSize.Md,
      isIconOnly: true,
      className: 'p-5',
    },
    {
      size: ButtonSize.Sm,
      isIconOnly: true,
      className: 'p-4',
    },
    {
      size: ButtonSize.Xs,
      isIconOnly: true,
      className: 'p-3',
    },
    {
      level: ButtonLevel.Ghost, // used for close buttons
      isIconOnly: true,
      className: 'text-quaternary',
    },
    /* Disabled */
    {
      level: ButtonLevel.Danger,
      isDisabled: true,
      className: 'opacity-40 pointer-events-none',
    },
    {
      level: ButtonLevel.Primary,
      isDisabled: true,
      className: 'bg-primary-disabled text-disabled border border-disabled pointer-events-none',
    },
    {
      level: ButtonLevel.Secondary,
      isDisabled: true,
      className: 'bg-primary border-disabled text-disabled pointer-events-none',
    },
    {
      level: ButtonLevel.Ghost,
      isDisabled: true,
      className: 'text-disabled pointer-events-none',
    },
    {
      level: ButtonLevel['Text-Primary'],
      isDisabled: true,
      className: 'text-disabled pointer-events-none',
    },
    {
      level: ButtonLevel['Text-Secondary'],
      isDisabled: true,
      className: 'text-disabled pointer-events-none',
    },
    {
      level: ButtonLevel['Text-Danger'],
      isDisabled: true,
      className: 'opacity-40 pointer-events-none',
    },
    {
      level: ButtonLevel.Gradient,
      isDisabled: true,
      className: 'bg-primary-disabled text-disabled pointer-events-none',
    },
    /* Hover/Active states (when not disabled) */
    {
      level: ButtonLevel.Danger,
      isDisabled: false,
      className: [
        /* No border radius for Danger (same as Primary) */
        /* Hovered */
        'data-[hovered]:bg-error-700',
        /* Active */
        'data-[pressed]:bg-error-700 data-[pressed]:text-primary-on-brand data-[pressed]:ring-offset-2 data-[pressed]:ring-offset-white',
      ],
    },
    {
      level: ButtonLevel.Primary,
      isDisabled: false,
      className: [
        /* No border radius for Primary */
        /* Hovered */
        'data-[hovered]:bg-brand-solid-hover',
        /* Active */
        'data-[pressed]:bg-brand-solid-hover data-[pressed]:text-primary-on-brand data-[pressed]:ring-offset-2 data-[pressed]:ring-offset-white',
      ],
    },
    {
      level: ButtonLevel.Secondary,
      isDisabled: false,
      className: [
        /* Hovered */
        'data-[hovered]:bg-primary-hover data-[hovered]:border-primary data-[hovered]:text-secondary-hover',
        /* Active */
        'data-[pressed]:border-gray-light-500 data-[pressed]:text-gray-light-900',
      ],
    },
    {
      level: ButtonLevel.Ghost,
      isDisabled: false,
      className: [
        /* Hovered */
        'data-[hovered]:bg-primary-hover data-[hovered]:text-tertiary-hover',
      ],
    },
    {
      level: ButtonLevel['Text-Primary'],
      isDisabled: false,
      className: [
        /* Hovered */
        'data-[hovered]:text-brand-secondary-hover data-[hovered]:underline',
        /* Active */
        'data-[pressed]:text-brand-secondary-hover',
      ],
    },
    {
      level: ButtonLevel['Text-Secondary'],
      isDisabled: false,
      className: [
        /* Hovered */
        'data-[hovered]:text-tertiary-hover data-[hovered]:underline',
        /* Active */
        'data-[pressed]:text-tertiary-hover',
      ],
    },
    {
      level: ButtonLevel['Text-Danger'],
      isDisabled: false,
      className: [
        /* Hovered */
        'data-[hovered]:text-error-primary-hover data-[hovered]:underline',
        /* Active */
        'data-[pressed]:text-error-primary-hover',
      ],
    },
    {
      level: ButtonLevel.Gradient,
      isDisabled: false,
      className: [
        /* No border radius for Gradient (same as Primary) */
        /* Hovered */
        'data-[hovered]:bg-gradient-neon-hover data-[pressed]:bg-gradient-neon-hover',
      ],
    },
    /* Loading */
    {
      level: ButtonLevel.Primary,
      isLoading: true,
      className: 'bg-brand-solid-hover pointer-events-none',
    },
    {
      level: ButtonLevel.Secondary,
      isLoading: true,
      className: 'bg-primary-hover border-primary text-secondary-hover pointer-events-none',
    },
    {
      level: ButtonLevel.Ghost,
      isLoading: true,
      className: 'bg-primary-hover text-tertiary-hover pointer-events-none',
    },
  ],
});
